import { useEffect, useId, useRef, type ReactNode } from 'react'
import Icon from '../../docs/Icon'

export type ModalProps = {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  /** The action buttons, shown at the foot of the modal. */
  actions?: ReactNode
  /**
   * A decision that must be made: no close button, and Escape and the
   * backdrop do nothing, so one of the actions has to be chosen.
   */
  forced?: boolean
  /** Lg for longer content. */
  size?: 'md' | 'lg'
}

/**
 * A modal for a decision or a short task, on the native <dialog>, which traps
 * focus, makes the page behind it inert and returns focus to the opener.
 */
export default function Modal({ open, onClose, title, children, actions, forced = false, size = 'md' }: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  const bodyId = useId()

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  return (
    <dialog
      ref={ref}
      className={`mds-modal mds-modal--${size}`}
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      // The native close event also fires when the effect above closes the
      // dialog; only a close the parent has not asked for, such as a form with
      // method="dialog", is reported.
      onClose={() => {
        if (open) onClose()
      }}
      onCancel={(event) => {
        event.preventDefault()
        if (!forced) onClose()
      }}
      onClick={(event) => {
        if (!forced && event.target === ref.current) onClose()
      }}
    >
      {open && (
        <div className="mds-modal__inner">
          <div className="mds-modal__head">
            <h2 id={titleId} className="mds-h3">
              {title}
            </h2>
            {!forced && (
              <button type="button" className="mds-icon-button" aria-label="Close" onClick={onClose}>
                <Icon name="close" />
              </button>
            )}
          </div>
          <div id={bodyId} className="mds-modal__body">
            {children}
          </div>
          {actions && <div className="mds-modal__actions">{actions}</div>}
        </div>
      )}
    </dialog>
  )
}
